import   React        ,
       { useState     }  from 'react';

import   classNames      from 'classnames';

import   styles          from './ChatModeration.module.scss';

import   ChatModerationBoard   from './ChatModerationBoard';
import   CommentModerationList from '../commentModerationManagment/CommentModerationList';
import   ReportModerationList  from '../reportModerationManagment/ReportModerationList';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';

/**
 * Onglet « Modération » de la régie : le chat en direct (trié par l'IA de
 * Google, plusieurs chaînes à la fois) et, à côté, la file des commentaires
 * vidéo qui existait déjà.
 *
 * Le chat reste monté quand on passe aux commentaires : son relevé, son signal
 * sonore et le compteur du titre continuent de tourner.
 *
 * Les super-admins (comptes ROLE_ADMIN : tubemarket, team) ont un troisième
 * onglet, Signalements : ce que les spectateurs signalent depuis le lecteur du
 * portail, sur les contenus de tous les créateurs.
 */

type Section = 'chat' | 'comments' | 'reports';

interface Props {
  onPendingCount? : (count: number) => void;
}

const ModerationPanel: React.FC<Props> = ({ onPendingCount }) => {

  const [ section     , setSection     ] = useState<Section>('chat');
  const [ pending     , setPending     ] = useState(0);
  const [ openReports , setOpenReports ] = useState(0);
  const [ comments    , setComments    ] = useState(0);

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const isSuperadmin = authentication.admin?.role === 'ADMIN';

  const handleCount = React.useCallback((count: number) => setPending(count), []);

  // Le titre de l'onglet Modération (« Modération (3) ») compte tout ce qui
  // attend : messages du chat et commentaires vidéo à valider et, pour un
  // super-admin, signalements à traiter — visibles ainsi depuis n'importe quel
  // autre onglet de la régie.
  React.useEffect(() => {
    onPendingCount?.(pending + comments + (isSuperadmin ? openReports : 0));
  }, [pending, comments, openReports, isSuperadmin, onPendingCount]);

  return (
    <div className={styles.moderation}>
      <nav className={styles.tabs} role="tablist" aria-label="Modération">
        <button type="button" role="tab" aria-selected={section === 'chat'}
                className={classNames(styles.tab, section === 'chat' && styles.tabOn)}
                onClick={() => setSection('chat')}>
          Chat en direct
          {pending > 0 && <span className={styles.count}>{pending}</span>}
        </button>
        <button type="button" role="tab" aria-selected={section === 'comments'}
                className={classNames(styles.tab, section === 'comments' && styles.tabOn)}
                onClick={() => setSection('comments')}>
          Commentaires vidéo
          {comments > 0 && <span className={styles.count}>{comments}</span>}
        </button>
        {isSuperadmin && (
          <button type="button" role="tab" aria-selected={section === 'reports'}
                  className={classNames(styles.tab, section === 'reports' && styles.tabOn)}
                  onClick={() => setSection('reports')}>
            Signalements
            {openReports > 0 && <span className={styles.count}>{openReports}</span>}
          </button>
        )}
      </nav>

      <div style={{ display: section === 'chat' ? 'block' : 'none' }}>
        <ChatModerationBoard onPendingCount={handleCount} />
      </div>

      {/* Monté en permanence, comme le chat : la file se relit et compte même
          quand on regarde un autre onglet. */}
      <div style={{ display: section === 'comments' ? 'block' : 'none' }}>
        <CommentModerationList onPendingCount={setComments} />
      </div>

      {/* Monté dès l'ouverture de l'écran, pour que l'onglet affiche le nombre
          de signalements à traiter avant même qu'on l'ouvre. */}
      {isSuperadmin && (
        <div style={{ display: section === 'reports' ? 'block' : 'none' }}>
          <ReportModerationList onOpenCount={setOpenReports} />
        </div>
      )}
    </div>
  );
};

export default ModerationPanel;
